import React, { useState, useEffect, useRef } from 'react';
import { Cpu, CheckCircle, Loader2, Brain } from 'lucide-react';
import BrandLogo from '@/components/common/BrandLogo';
import MapleOSLogo from '@/components/common/MapleOSLogo';
import { DotSwarm } from './DotSwarm';

interface BootScreenProps {
  isVisible: boolean;
  onComplete: () => void;
}

type BootStep = {
  text: string;
  icon: React.ReactNode;
  duration: number; // relative units, not ms
  thinking?: boolean;
};

export const BootScreen: React.FC<BootScreenProps> = ({ isVisible, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [statusHistory, setStatusHistory] = useState<
    Array<{ text: string; icon: React.ReactNode; thinking?: boolean }>
  >([]);
  const [showLogo, setShowLogo] = useState(false);
  const [dots, setDots] = useState('');
  const currentStepRef = useRef(0);

  // Match Auth page MapleOS theme tokens (without relying on data-brand in OS routes)
  const loginBg = 'bg-[hsl(220_20%_8%)]';
  const loginFg = 'text-[hsl(220_10%_85%)]';
  const accent = 'text-[hsl(75_100%_42%)]';

  const bootSteps: BootStep[] = [
    {
      text: 'Initializing MapleOS…',
      icon: <MapleOSLogo iconOnly className={`h-5 w-5 ${accent}`} />,
      duration: 35,
    },
    {
      text: 'Loading system components…',
      icon: <Cpu size={20} className={accent} />,
      duration: 35,
    },
    {
      text: 'Starting desktop environment…',
      icon: <MapleOSLogo iconOnly className="h-5 w-5 text-white/90" />,
      duration: 35,
    },
    {
      text: 'Thinking — personalizing your desktop',
      icon: <Brain size={20} className={accent} />,
      duration: 45,
      thinking: true,
    },
    {
      text: 'Ready',
      icon: <CheckCircle size={20} className={accent} />,
      duration: 15,
    },
  ];

  useEffect(() => {
    if (!isVisible) return;

    setProgress(0);
    setStatusHistory([
      { text: bootSteps[0].text, icon: bootSteps[0].icon, thinking: bootSteps[0].thinking },
    ]);
    setShowLogo(false);
    currentStepRef.current = 0;

    const logoTimer = setTimeout(() => setShowLogo(true), 260);

    let progressInterval: NodeJS.Timeout | undefined;
    const totalDuration = bootSteps.reduce((sum, step) => sum + step.duration, 0);

    const updateProgress = () => {
      progressInterval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev + 100 / totalDuration;

          let accumulated = 0;
          let targetIndex = bootSteps.length - 1;
          for (let i = 0; i < bootSteps.length; i++) {
            accumulated += bootSteps[i].duration;
            if (newProgress <= (accumulated / totalDuration) * 100) {
              targetIndex = i;
              break;
            }
          }

          if (targetIndex > currentStepRef.current) {
            currentStepRef.current = targetIndex;
            const s = bootSteps[targetIndex];
            setStatusHistory((prevH) => [...prevH, { text: s.text, icon: s.icon, thinking: s.thinking }]);
          }

          if (newProgress >= 100) {
            if (progressInterval) clearInterval(progressInterval);
            setTimeout(() => onComplete(), 700);
            return 100;
          }
          return newProgress;
        });
      }, 60);
    };

    const progressTimer = setTimeout(updateProgress, 760);

    return () => {
      clearTimeout(logoTimer);
      clearTimeout(progressTimer);
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isVisible, onComplete]);

  useEffect(() => {
    const latest = statusHistory[statusHistory.length - 1];
    if (!latest?.thinking) {
      setDots('');
      return;
    }
    const t = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : d + '.'));
    }, 340);
    return () => clearInterval(t);
  }, [statusHistory]);

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-[10001] overflow-hidden ${loginBg}`}>
      {/* Same base background vibe as login */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/15 to-black/35" />

      {/* Soft green ambience (subtle) */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(170,255,60,0.14)_0%,transparent_62%)] blur-3xl" />
        <div className="absolute bottom-[-240px] left-[12%] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,rgba(170,255,60,0.10)_0%,transparent_70%)] blur-3xl" />
      </div>

      {/* Fine grid */}
      <div className="absolute inset-0 opacity-[0.025]">
        <div className="h-full w-full bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      {/* Animated particles */}
      <DotSwarm active={isVisible} dotCount={18} />

      {/* Main content */}
      <div className="relative flex h-full w-full items-center justify-center px-8">
        <div className="w-full max-w-md text-center">
          <div
            className={`mb-10 transition-all duration-1000 ${
              showLogo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
          >
            {/* Full logo (icon + wordmark), no box */}
            <div className="mb-4 flex justify-center">
              <BrandLogo size={52} logoClassName={loginFg} />
            </div>
            <p className="text-sm text-white/60">A calm, focused desktop that gets out of your way.</p>
          </div>

          <div
            className={`transition-all duration-500 ${
              showLogo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {/* Status */}
            <div className="mb-6">
              {(() => {
                const latest = statusHistory[statusHistory.length - 1];
                return (
                  <div className="mx-auto flex max-w-sm items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black/30 ring-1 ring-white/10">
                      {latest?.thinking ? (
                        <Loader2 size={18} className="animate-spin text-white/90" />
                      ) : (
                        latest?.icon
                      )}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-white/90">
                        {latest?.text}
                        {latest?.thinking ? dots : ''}
                      </div>
                      <div className="mt-0.5 text-xs text-white/45">
                        {latest?.thinking ? 'Calibrating preferences & restoring session' : 'Please wait…'}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Progress */}
            <div className="mx-auto max-w-sm">
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/10 ring-1 ring-white/10">
                <div
                  className="h-full rounded-full bg-[linear-gradient(90deg,rgba(170,255,60,0.15),rgba(170,255,60,0.90),rgba(255,255,255,0.65),rgba(170,255,60,0.90),rgba(170,255,60,0.15))] shadow-[0_0_20px_rgba(170,255,60,0.35)] transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-white/45">{progress.toFixed(0)}% complete</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};